import React from 'react';

import { PartnerAccountManagerSummary } from 'containers/PartnerAccountManager/PartnerAccountManagerSummary';
import { PartnerAccountManagerPasswordEditor } from 'containers/PartnerAccountManager/PartnerAccountManagerPasswordEditor';
import { PartnerAccountManagerProfileEditor } from 'containers/PartnerAccountManager/PartnerAccountManagerProfileEditor';
import { ModalLayerComponent } from 'design/modalLayer';
import { SidebarModalWindowComponent } from 'design/sidebarModal';
import { PartnerAccountManagerHeader } from './PartnerAccountManagerHeader';
import { PartnerFinancesTable } from 'containers/PartnerFinancesTable';
import { useUserPartnerFinances } from 'hooks/useUserPartnerFinances';
import { useUserPartnerFinanceMonthReports } from 'hooks/useUserPartnerFinanceMonthReports';
import { useAppDispatcher } from 'hooks/useAppDispatcher';

export type PartnerAccountManagerScreen =
  | 'SUMMARY'
  | 'FINANCES'
  | 'PROFILE_EDITOR'
  | 'PASSWORD_EDITOR';

type Props = {
  onClose(): void;
  initialScreen: PartnerAccountManagerScreen;
};

export const PartnerAccountManager = (props: Props) => {
  const { onClose: close, initialScreen } = props;

  const partnerFinances = useUserPartnerFinances();

  const partnerFinanceMonthReports = useUserPartnerFinanceMonthReports();

  const [screen, setScreen] = React.useState<PartnerAccountManagerScreen>(
    initialScreen
  );

  const dispatch = useAppDispatcher();

  const manageClient = React.useCallback(
    (clientId, organizationName) => {
      close();
      dispatch({
        name: 'PartnerAccountManager__ManageClient',
        clientId,
        organizationName,
      });
    },
    [close, dispatch]
  );

  const goToSummaryScreen = React.useCallback(() => {
    setScreen('SUMMARY');
  }, []);

  const goToFinancesScreen = React.useCallback(() => {
    setScreen('FINANCES');
  }, []);

  const goToProfileEditorScreen = React.useCallback(() => {
    setScreen('PROFILE_EDITOR');
  }, []);

  const goToPasswordEditorScreen = React.useCallback(() => {
    setScreen('PASSWORD_EDITOR');
  }, []);

  const handleMisclick = React.useCallback(() => {
    if (screen === 'SUMMARY') {
      close();
    }
  }, [close, screen]);

  const content = React.useMemo(() => {
    switch (screen) {
      case 'SUMMARY':
        return (
          <>
            <PartnerAccountManagerHeader
              currentScreen={screen}
              onViewSummary={goToSummaryScreen}
              onViewFinances={goToFinancesScreen}
            />
            <PartnerAccountManagerSummary
              onEditProfile={goToProfileEditorScreen}
              onEditPassword={goToPasswordEditorScreen}
            />
          </>
        );
      case 'FINANCES':
        return (
          <>
            <PartnerAccountManagerHeader
              currentScreen={screen}
              onViewSummary={goToSummaryScreen}
              onViewFinances={goToFinancesScreen}
            />
            {partnerFinances && (
              <PartnerFinancesTable
                partnerFinances={partnerFinances}
                partnerFinanceMonthReports={partnerFinanceMonthReports}
                onShowClientDetails={manageClient}
              />
            )}
          </>
        );
      case 'PROFILE_EDITOR':
        return (
          <PartnerAccountManagerProfileEditor
            onCancel={goToSummaryScreen}
            onEdited={goToSummaryScreen}
          />
        );
      case 'PASSWORD_EDITOR':
        return (
          <PartnerAccountManagerPasswordEditor
            onCancel={goToSummaryScreen}
            onEdited={goToSummaryScreen}
          />
        );
      default:
        return null;
    }
  }, [
    screen,
    goToSummaryScreen,
    goToFinancesScreen,
    goToProfileEditorScreen,
    goToPasswordEditorScreen,
    partnerFinances,
    partnerFinanceMonthReports,
    manageClient,
  ]);

  return (
    <ModalLayerComponent theme="transparent">
      <SidebarModalWindowComponent
        onClose={close}
        onMisclick={handleMisclick}
        onEscape={close}
      >
        {content}
      </SidebarModalWindowComponent>
    </ModalLayerComponent>
  );
};
